/**
 * Internal Short URL API Contract (SCH-1835 U1)
 *
 * POST /internal/shorturl
 *
 * Mints a `jrny.to` short link on the shared INTERNAL_API_KEY, for callers that
 * hold no Clerk session: the customer-pulse runner and agent scripts. Agents may
 * never mint Clerk tokens, and `POST /v1/shorturl` needs a school-admin session.
 *
 * The link belongs to NO school (`schoolId` null), so its clicks never land in
 * any school's analytics. That is why the body refuses `schoolId` and
 * `contentId`: a caller cannot attach the link to a school or a content record.
 *
 * An optional vanity `code` (e.g. `jrny.to/rj-debrief`) is accepted (SCH-2909).
 * Codes are one global namespace shared with `POST /v1/shorturl`, so a code
 * already taken by another target or another surface is a 409.
 *
 * Only the wire shape lives here. The endpoint's error enum stays beside its
 * handler in the backend, like every other `/internal/*` contract in this folder.
 */

import { z } from 'zod'

export const InternalCreateShortUrlRequestSchema = z
  .object({
    /**
     * HTTPS ONLY. The shared service accepts `http:` too; this surface is
     * narrower because every link it mints is sent to a person outside the
     * platform, and a plain-http target would be the one downgrade in the chain.
     */
    targetUrl: z.url({ protocol: /^https$/, error: 'targetUrl must be an https:// URL' }),
    title: z.string().max(255).optional(),
    expiresAt: z.iso
      .datetime({ offset: true })
      // A past expiry mints a link that 404s on its first click, and the reuse
      // lookup skips expired rows, so every retry would mint another dead one.
      .refine(value => new Date(value).getTime() > Date.now(), { message: 'expiresAt must be in the future' })
      .optional(),
    utmCampaign: z.string().max(100).optional(),
    /**
     * Custom short code; generated when omitted. Same shape as `POST /v1/shorturl`.
     * Reserved words (`api`, `admin`, ...) are refused by the backend with a 400.
     * With a code, idempotency is by CODE: a retry for the same code and target
     * returns the existing link with `reused: true`; a code already in use for
     * anything else is a 409.
     */
    code: z
      .string()
      .min(3)
      .max(50)
      .regex(/^[a-zA-Z0-9-]+$/, { message: 'code may contain only letters, numbers and hyphens' })
      .optional()
  })
  // Strict, so a caller that sends `schoolId` or `contentId` gets a 400 instead
  // of a link that silently ignored the field it thought it set.
  .strict()
export type InternalCreateShortUrlRequest = z.infer<typeof InternalCreateShortUrlRequestSchema>

export const InternalCreateShortUrlResponseSchema = z.object({
  code: z.string(),
  /** Absolute, e.g. `https://jrny.to/abc123`. */
  shortUrl: z.string(),
  targetUrl: z.string(),
  expiresAt: z.string().nullable(),
  clicks: z.number().int(),
  /**
   * True when an active, unexpired link this endpoint minted earlier was
   * returned instead of a new one: for the same `targetUrl` and `utmCampaign`
   * or, when `code` was sent, for that code and the same target. Its title and
   * expiry are the ORIGINAL request's, not this one's.
   */
  reused: z.boolean()
})
export type InternalCreateShortUrlResponse = z.infer<typeof InternalCreateShortUrlResponseSchema>
